import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { useAuth } from "../platform";
import type { OpenKiki } from "./useEventsList";

export type BuddyPreferences = {
  // Profile-card config.
  interests?: string[];
  // Legacy audience-filter fields. Still accepted by the server so posts
  // created before the rework remain editable.
  ageMin?: number;
  ageMax?: number;
  genders?: string[];
  orientations?: string[];
};

export type BuddyApplicantSummary = {
  id: string;
  firstName: string;
  lastName: string | null;
  profileImageUrl: string | null;
};

export type BuddyRequestItem = {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string | null;
    profileImageUrl: string | null;
  };
  preferences: BuddyPreferences | null;
  note: string | null;
  createdAt: string;
  applicationCount: number;
  acceptedApplicants: BuddyApplicantSummary[];
  applied: boolean;
  mine: boolean;
};

export type BuddyApplicationStatus = "pending" | "accepted" | "declined";

export type BuddyViewerStatus =
  | "owner"
  | "accepted"
  | "pending"
  | "declined"
  | "visitor";

// Profile context an applicant opted into sharing when reaching out.
export type BuddyApplicationSharedProfile = {
  instagram?: string;
  interests?: string[];
};

export type OwnerBuddyApplication = {
  id: string;
  status: BuddyApplicationStatus;
  note: string | null;
  sharedProfile: BuddyApplicationSharedProfile | null;
  createdAt: string;
  user: BuddyApplicantSummary;
};

export type BuddyRequestStatus = "active" | "fulfilled" | "cancelled";

export type BuddyRequestDetail = {
  id: string;
  status?: BuddyRequestStatus;
  user: BuddyApplicantSummary;
  preferences: BuddyPreferences | null;
  note: string | null;
  createdAt: string;
  applicationCount: number;
  acceptedApplicants: BuddyApplicantSummary[];
  viewerStatus: BuddyViewerStatus;
  viewerApplication: {
    id: string;
    status: BuddyApplicationStatus;
    note: string | null;
    createdAt: string;
  } | null;
  ownerApplications: OwnerBuddyApplication[] | null;
  mine: boolean;
};

export function useEventBuddies(slug: string | null) {
  return useQuery({
    queryKey: ["eventBuddies", slug],
    queryFn: async () =>
      api.get<BuddyRequestItem[]>(`/events/public/${slug}/buddies`),
    enabled: !!slug,
  });
}

// Predicate-based invalidation: matches every `eventBuddies` / `eventBuddy`
// query in the cache regardless of how its slug parameter was serialized.
// This avoids subtle mismatches (e.g. slug=null vs slug="") between the
// list query and the mutation hook that would cause an exact-key invalidate
// to silently no-op. `refetchType: "all"` also forces a refetch even if no
// active observer is currently mounted.
function invalidateAllBuddyQueries(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        Array.isArray(k) &&
        (k[0] === "eventBuddies" || k[0] === "eventBuddy")
      );
    },
    refetchType: "all",
  });
}

export function useCreateBuddyRequest(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { preferences?: BuddyPreferences; note?: string }) =>
      api.post<{ id: string }>(`/events/${slug}/buddies`, body),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}

export function useUpdateBuddyRequest(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      preferences,
      note,
    }: {
      id: string;
      preferences?: BuddyPreferences;
      note?: string;
    }) =>
      api.patch<unknown>(`/events/${slug}/buddies/${id}`, { preferences, note }),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}

// Owner closes ("fulfilled") or reopens ("active") a kiki.
export function useSetKikiStatus(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "active" | "fulfilled" }) =>
      api.patch<unknown>(`/events/${slug}/buddies/${id}/status`, { status }),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
      qc.invalidateQueries({ queryKey: ["eventsList"] });
    },
  });
}

export function useDeleteBuddyRequest(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.delete<unknown>(`/events/${slug}/buddies/${id}`),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}

export function useApplyToBuddy(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      note,
      sharedProfile,
    }: {
      id: string;
      note?: string;
      sharedProfile?: BuddyApplicationSharedProfile;
    }) =>
      api.post<unknown>(`/events/${slug}/buddies/${id}/apply`, { note, sharedProfile }),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}

export function useBuddyRequestDetail(slug: string | null, id: string | null) {
  return useQuery({
    queryKey: ["eventBuddy", slug, id],
    queryFn: async () =>
      api.get<BuddyRequestDetail>(`/events/public/${slug}/buddies/${id}`),
    enabled: !!slug && !!id,
  });
}

export type KikiActivityInfo = {
  name: string;
  emoji?: string;
  date?: string;
  time?: string;
  location?: string;
};

export type KikiEventContext = {
  slug: string | null;
  name: string | null;
  date: string;
  time: string;
  endTime: string | null;
  description: string | null;
  coverImageUrl: string | null;
  types: string[];
  posterLabel: string;
  venue: {
    name: string | null;
    neighborhood: string | null;
    city: string | null;
    address: string | null;
  };
};

// Detail keyed by kiki id alone (works for event-tied and standalone kikis).
// Carries the resolved event context or the standalone activity info so the
// page can render without an event slug.
export type KikiDetail = BuddyRequestDetail & {
  event: KikiEventContext | null;
  activityInfo: KikiActivityInfo | null;
};

export function useKikiDetail(id: string | null) {
  return useQuery({
    queryKey: ["eventBuddy", "id", id],
    queryFn: async () => api.get<KikiDetail>(`/events/kikis/${id}`),
    enabled: !!id,
  });
}

// The signed-in user's own kikis — hosted + ones they've been accepted into.
// Shaped like OpenKiki so the dashboard can render them with KikiCarousel.
export function useMyKikis() {
  // /kikis/mine is auth-only — never fire it for signed-out visitors (it would
  // 401 on public surfaces that mount this hook).
  const { isSignedIn } = useAuth();
  return useQuery({
    queryKey: ["my-kikis"],
    queryFn: async () => api.get<OpenKiki[]>(`/events/kikis/mine`),
    enabled: isSignedIn,
  });
}


export function useWithdrawBuddyApplication(slug: string, requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (applicationId: string) =>
      api.delete<unknown>(`/events/${slug}/buddies/${requestId}/applications/${applicationId}`),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}

export function useUpdateBuddyApplicationStatus(slug: string, requestId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      applicationId,
      status,
    }: {
      applicationId: string;
      status: BuddyApplicationStatus;
    }) =>
      api.patch<unknown>(
        `/events/${slug}/buddies/${requestId}/applications/${applicationId}`,
        { status },
      ),
    onSuccess: async () => {
      await invalidateAllBuddyQueries(qc);
    },
  });
}
